import axios, {
  AxiosError,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import ensureAuthenticatedStorage from "@/contexts/ensureAuthenticated";
import { navigateToLogin } from "@/utils/navigation";
import { createStandaloneToast } from "@chakra-ui/react";

export const VITE_FINANCIAL_API_BASE_URL =
  import.meta.env.VITE_FINANCIAL_API_BASE_URL || "http://localhost:3333";

export const getAuthToken = (): string | null => {
  return localStorage.getItem("session");
};

export const createAuthHeaders = () => {
  const token = getAuthToken();
  return {
    Authorization: token ? `Bearer ${token}` : "",
    "Cache-Control": import.meta.env.DEV
      ? "no-cache, no-store, must-revalidate"
      : "private, max-age=60",
    Pragma: import.meta.env.DEV ? "no-cache" : "",
    Expires: import.meta.env.DEV ? "0" : "",
  };
};

const axiosRefresh = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
}> = [];

const processQueue = (error: AxiosError | null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(null);
    }
  });
  failedQueue = [];
};

const handleLogout = () => {
  localStorage.removeItem("session");
  localStorage.removeItem("refreshToken");
  const { removeAuthentication } = ensureAuthenticatedStorage.getState();
  removeAuthentication();

  const { toast } = createStandaloneToast();
  toast({
    title: "Sessão expirada.",
    description: "Por favor faça o login, novamente.",
    status: "warning",
    duration: 5000,
    isClosable: true,
  });

  navigateToLogin();
};

const setupResponseInterceptor = () => {
  axios.interceptors.response.use(
    (response: AxiosResponse) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(() => {
              return axios(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          const refreshToken = localStorage.getItem("refreshToken");
          if (!refreshToken) {
            processQueue(error);
            handleLogout();
            return reject(error);
          }

          axiosRefresh
            .post("/api/auth/refresh-token", {
              refreshToken,
            })
            .then(({ data }) => {
              localStorage.setItem("session", data.session);
              localStorage.setItem("refreshToken", data.refreshToken);

              if (originalRequest.headers) {
                originalRequest.headers.Authorization = `Bearer ${data.session}`;
              }
              resolve(axios(originalRequest));
            })
            .catch((err) => {
              processQueue(err);
              handleLogout();
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }

      return Promise.reject(error);
    }
  );
};

setupResponseInterceptor();

// Criar instância específica para o financial service
const axiosFinancial = axios.create({
  baseURL: VITE_FINANCIAL_API_BASE_URL,
});

export { axiosFinancial };
